import { useState, useEffect } from "react";
import { WorkflowData } from "../WorkflowEditor";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { FileText, AlertTriangle, CheckCircle2, ExternalLink, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";

interface DependenciesTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

export function DependenciesTab({ data, onChange }: DependenciesTabProps) {
  const [documentTemplates, setDocumentTemplates] = useState<any[]>([]);
  const [relatedWorkflows, setRelatedWorkflows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeInstances, setActiveInstances] = useState(0);

  useEffect(() => {
    loadDependencies();
  }, [data]);

  async function loadDependencies() {
    setLoading(true);
    try {
      // Extract document template IDs from phases
      const documentIds = new Set<string>();
      if (data.phases) {
        data.phases.forEach((phase: any) => {
          if (phase.documents) {
            phase.documents.forEach((doc: any) => {
              if (doc.template_id) {
                documentIds.add(doc.template_id);
              }
            });
          }
        });
      }

      // Load document templates
      if (documentIds.size > 0) {
        const { data: templates, error: templatesError } = await supabase
          .from('document_template_library')
          .select('id, template_name, template_category, is_active')
          .in('id', Array.from(documentIds));

        if (templatesError) throw templatesError;
        setDocumentTemplates(templates || []);
      } else {
        setDocumentTemplates([]);
      }

      // Load related workflows (same category)
      const { data: workflows, error: workflowsError } = await supabase
        .from('workflows')
        .select('id, title, workflow_key, is_active')
        .eq('category', data.category)
        .neq('id', data.id || '');

      if (workflowsError) throw workflowsError;
      setRelatedWorkflows(workflows || []);

      // Count active instances
      if (data.id) {
        const { count } = await supabase
          .from('task_workflows')
          .select('*', { count: 'exact', head: true })
          .eq('workflow_id', data.id)
          .is('completed_at', null);

        setActiveInstances(count || 0);
      }
    } catch (error: any) {
      console.error('Error loading dependencies:', error);
    } finally {
      setLoading(false);
    }
  }

  const missingTemplates = data.phases?.flatMap((phase: any) =>
    (phase.documents || []).filter((doc: any) =>
      doc.template_id && !documentTemplates.find(t => t.id === doc.template_id)
    )
  ) || [];

  const inactiveTemplates = documentTemplates.filter(t => !t.is_active);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold">Dependencies & Related Items</h2>
        <p className="text-sm text-muted-foreground">
          Document templates, related workflows, and active instances
        </p>
      </div>

      {/* Active Instances Warning */}
      {activeInstances > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This workflow has <strong>{activeInstances}</strong> active instance{activeInstances !== 1 ? 's' : ''}.
            Changes will not affect workflows already in progress.
          </AlertDescription>
        </Alert>
      )}

      {/* Document Templates */}
      <Card className="p-4">
        <h3 className="font-medium flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5" />
          Document Templates ({documentTemplates.length})
        </h3>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading templates...</p>
        ) : documentTemplates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No document templates linked to this workflow
          </p>
        ) : (
          <div className="space-y-2">
            {documentTemplates.map((template) => (
              <div key={template.id} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  {template.is_active ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{template.template_name}</p>
                    <p className="text-xs text-muted-foreground">{template.template_category}</p>
                  </div>
                </div>
                <Badge variant={template.is_active ? "default" : "secondary"}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {/* Warnings */}
        {missingTemplates.length > 0 && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{missingTemplates.length}</strong> document template{missingTemplates.length !== 1 ? 's are' : ' is'} missing or deleted.
              Please review the Documents tab.
            </AlertDescription>
          </Alert>
        )}

        {inactiveTemplates.length > 0 && (
          <Alert className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{inactiveTemplates.length}</strong> template{inactiveTemplates.length !== 1 ? 's are' : ' is'} inactive.
              Users won't be able to generate these documents.
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {/* Related Workflows */}
      <Card className="p-4">
        <h3 className="font-medium flex items-center gap-2 mb-4">
          <Workflow className="h-5 w-5" />
          Related Workflows ({relatedWorkflows.length})
        </h3>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading related workflows...</p>
        ) : relatedWorkflows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No related workflows in the same category
          </p>
        ) : (
          <div className="space-y-2">
            {relatedWorkflows.slice(0, 5).map((workflow) => (
              <div key={workflow.id} className="flex items-center justify-between p-2 border rounded">
                <div>
                  <p className="text-sm font-medium">{workflow.title}</p>
                  <p className="text-xs text-muted-foreground">{workflow.workflow_key}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={workflow.is_active ? "default" : "outline"}>
                    {workflow.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={`/admin/templates/workflows/${workflow.id}`} target="_blank" rel="noopener">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            ))}
            {relatedWorkflows.length > 5 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                And {relatedWorkflows.length - 5} more...
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Workflow Prerequisites */}
      {data.prerequisites && data.prerequisites.length > 0 && (
        <Card className="p-4">
          <h3 className="font-medium mb-4">Prerequisites</h3>
          <ul className="space-y-2">
            {data.prerequisites.map((prereq: string, index: number) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                {prereq}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
